import React from "react";
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  Package,
  AlertTriangle,
  Clock,
  Boxes,
  Truck,
  ArrowRight,
  Sparkles,
  Layers,
  Building2,
  CheckCircle2,
  AlertCircle
} from "lucide-react";
import { useApp } from "../../app/context/AppContext";
import { useAuth } from "../../app/context/AuthContext";
import { formatCurrency, formatDate } from "../../utils/formatters";
import { KPICard } from "../../components/common/KPICard";
import { Link, useNavigate } from "react-router-dom";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid
} from "recharts";

export function DashboardModule() {
  const { metrics, salesOrders, purchaseOrders, warehouses, batches, products } = useApp();
  const { role, currentUser } = useAuth();
  const navigate = useNavigate();

  // 30-day simulated sales trend chart
  const salesTrendData = [
    { day: "01 Aug", sales: 12400, purchases: 18000, profit: 1800 },
    { day: "02 Aug", sales: 16800, purchases: 0, profit: 2400 },
    { day: "03 Aug", sales: 21500, purchases: 42000, profit: 3100 },
    { day: "04 Aug", sales: 14200, purchases: 0, profit: 2100 },
    { day: "05 Aug", sales: 28900, purchases: 50000, profit: 4200 },
    { day: "06 Aug", sales: 34571, purchases: 0, profit: 4800 },
    { day: "07 Aug", sales: 18900, purchases: 112760, profit: 2900 },
    { day: "08 Aug (Today)", sales: metrics.totalSalesAmount || 36400, purchases: 72300, profit: metrics.totalProfit || 5200 }
  ];

  // Category Revenue
  const categoryData = [
    { name: "Edible Oils", revenue: 84500, margin: "14%" },
    { name: "Grains & Rice", revenue: 72800, margin: "18%" },
    { name: "Flours & Atta", revenue: 49000, margin: "16%" },
    { name: "Snacks & Biscuits", revenue: 38200, margin: "22%" },
    { name: "Spices & Dal", revenue: 34100, margin: "24%" },
    { name: "Home Care", revenue: 29800, margin: "20%" }
  ];

  // Stock Distribution across 3 Warehouses
  const warehouseDistribution = warehouses.map((wh, idx) => {
    const whBatches = batches.filter((b) => b.warehouseId === wh.id && b.status !== "Expired");
    const totalVal = whBatches.reduce((sum, b) => sum + (Number(b.qtyRemaining) || 0) * (Number(b.unitCost) || 0), 0);
    const colors = ["#10b981", "#0284c7", "#f59e0b"];
    return {
      name: wh.name.split("(")[0].trim(),
      value: totalVal > 0 ? totalVal : 15000 * (3 - idx),
      color: colors[idx % colors.length]
    };
  });

  return (
    <div className="space-y-6">
      {/* Welcome Banner */}
      <div className="rounded-3xl bg-gradient-to-r from-slate-900 via-slate-800 to-emerald-950 p-6 sm:p-8 text-white shadow-xl relative overflow-hidden">
        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="space-y-1.5">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-300 text-xs font-semibold">
              <Sparkles className="w-3.5 h-3.5" />
              Enterprise Central Dashboard • Multi-Warehouse Real-Time
            </div>
            <h2 className="text-2xl sm:text-3xl font-extrabold font-display">
              Namaste, {currentUser?.name}
            </h2>
            <p className="text-xs sm:text-sm text-slate-300 max-w-xl">
              One system. Every product. Every location. Real-time. Managing{" "}
              <span className="text-emerald-400 font-semibold">{warehouses.length} Warehouses</span>,{" "}
              <span className="text-emerald-400 font-semibold">{products.length} Products</span>, and{" "}
              <span className="text-emerald-400 font-semibold">{batches.length} Active Batches</span>.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Link
              to="/sales"
              className="px-4 py-2 text-xs font-bold text-slate-900 bg-white hover:bg-slate-100 rounded-xl shadow transition"
            >
              Launch Retail POS
            </Link>
            <Link
              to="/procurement"
              className="px-4 py-2 text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-500 rounded-xl shadow-lg shadow-emerald-600/30 transition"
            >
              + Create Purchase Order
            </Link>
          </div>
        </div>

        {/* Ambient Gradient Glow */}
        <div className="absolute -right-12 -bottom-12 w-64 h-64 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />
      </div>

      {/* 8 Dynamic Live KPI Tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title="Total Sales (M-T-D)"
          value={formatCurrency(metrics.totalSalesAmount)}
          subtitle={`${salesOrders.length} Completed Orders`}
          trend="+18.4% vs last mo"
          trendType="up"
          color="emerald"
          icon={DollarSign}
          onClick={() => navigate("/sales")}
        />
        <KPICard
          title="Total Purchases"
          value={formatCurrency(metrics.totalPurchasesAmount)}
          subtitle={`${purchaseOrders.length} Orders in Pipeline`}
          trend="8 Active Vendors"
          trendType="neutral"
          color="blue"
          icon={Truck}
          onClick={() => navigate("/procurement")}
        />
        <KPICard
          title="Gross Profit & Margin"
          value={formatCurrency(metrics.totalProfit)}
          subtitle={`Average Margin: ${metrics.grossMargin}%`}
          trend={`${metrics.grossMargin}% Net`}
          trendType="up"
          color="purple"
          icon={TrendingUp}
          onClick={() => navigate("/analytics")}
        />
        <KPICard
          title="Total Stock Value"
          value={formatCurrency(metrics.totalStockValue)}
          subtitle="Across 3 Warehouses"
          trend="Real-time FIFO Cost"
          trendType="neutral"
          color="indigo"
          icon={Boxes}
          onClick={() => navigate("/inventory")}
        />

        <KPICard
          title="Low Stock SKUs"
          value={`${metrics.lowStockCount} Items`}
          subtitle="At or below reorder level"
          trend="Action Needed"
          trendType={metrics.lowStockCount > 0 ? "down" : "neutral"}
          color="rose"
          icon={AlertTriangle}
          onClick={() => navigate("/dead-stock")}
        />
        <KPICard
          title="Expiring Soon (<45d)"
          value={`${metrics.expiringSoonCount} Batches`}
          subtitle="Discount or Return"
          trend={metrics.expiringSoonCount > 0 ? "Critical Watch" : "Safe"}
          trendType={metrics.expiringSoonCount > 0 ? "down" : "up"}
          color="amber"
          icon={Clock}
          onClick={() => navigate("/batch-expiry")}
        />
        <KPICard
          title="Dead Stock Capital"
          value={formatCurrency(metrics.deadStockValue)}
          subtitle={`${metrics.deadStockProducts?.length || 0} slow-moving items`}
          trend="Locked Working Capital"
          trendType="down"
          color="rose"
          icon={Package}
          onClick={() => navigate("/dead-stock")}
        />
        <KPICard
          title="Outstanding Receivables"
          value={formatCurrency(metrics.outstandingReceivables)}
          subtitle={`Payables: ${formatCurrency(metrics.outstandingPayables)}`}
          trend="Net Credit Balance"
          trendType="neutral"
          color="blue"
          icon={Building2}
          onClick={() => navigate("/customers")}
        />
      </div>

      {/* Critical "Needs Attention" Panel */}
      <div className="rounded-2xl border border-rose-200/80 dark:border-rose-900/60 bg-rose-50/40 dark:bg-rose-950/20 p-5 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-rose-600 dark:text-rose-400 animate-pulse" />
            <h3 className="text-sm font-bold text-slate-900 dark:text-white font-display">
              Operational Priority Attention Items (Deep Links)
            </h3>
          </div>
          <span className="text-[11px] font-mono text-rose-600 dark:text-rose-400 font-semibold">
            {metrics.lowStockCount + metrics.expiringSoonCount + 2} Alerts Active
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 text-xs">
          {/* Low stock item card */}
          {metrics.lowStockItems.slice(0, 2).map((item) => (
            <div
              key={item.id}
              onClick={() => navigate("/dead-stock")}
              className="p-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl hover:border-rose-400 dark:hover:border-rose-700 cursor-pointer transition shadow-xs flex flex-col justify-between"
            >
              <div>
                <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-300">
                  Low Stock Alert
                </span>
                <p className="font-semibold text-slate-900 dark:text-white mt-1 line-clamp-1">
                  {item.name}
                </p>
                <p className="text-[11px] text-slate-500">Reorder Level: {item.reorderLevel} units</p>
              </div>
              <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-100 dark:border-slate-800 text-emerald-600 dark:text-emerald-400 font-semibold text-[11px]">
                <span>1-Click PO</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </div>
            </div>
          ))}

          {/* Expiring item card */}
          {metrics.expiringSoonBatches.slice(0, 2).map((batch) => (
            <div
              key={batch.id}
              onClick={() => navigate("/batch-expiry")}
              className="p-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl hover:border-amber-400 dark:hover:border-amber-700 cursor-pointer transition shadow-xs flex flex-col justify-between"
            >
              <div>
                <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300">
                  Expiring Batch
                </span>
                <p className="font-semibold text-slate-900 dark:text-white mt-1 line-clamp-1">
                  {batch.productName}
                </p>
                <p className="text-[11px] text-slate-500 font-mono">
                  Batch: {batch.batchNo} • {batch.qtyRemaining} units
                </p>
              </div>
              <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-100 dark:border-slate-800 text-amber-600 dark:text-amber-400 font-semibold text-[11px]">
                <span>Discount / Return</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Visual Analytics Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Sales & Purchases Trend */}
        <div className="rounded-2xl border border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-bold text-slate-900 dark:text-white font-display">
                Sales & Procurement Velocity (30-Day Trend)
              </h3>
              <p className="text-xs text-slate-500">Gross revenue vs purchase orders</p>
            </div>
            <span className="text-xs font-mono font-semibold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/60 px-2.5 py-1 rounded-lg">
              Live Feed
            </span>
          </div>

          <div className="h-64 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={salesTrendData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="salesGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="purchaseGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#0284c7" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#0284c7" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.2)" />
                <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip
                  formatter={(val) => [`₹${val.toLocaleString("en-IN")}`, ""]}
                  contentStyle={{ backgroundColor: "#0f172a", borderRadius: "12px", border: "none", color: "#fff", fontSize: "12px" }}
                />
                <Legend />
                <Area type="monotone" dataKey="sales" name="Sales (₹)" stroke="#10b981" fillOpacity={1} fill="url(#salesGrad)" strokeWidth={2} />
                <Area type="monotone" dataKey="purchases" name="Purchases (₹)" stroke="#0284c7" fillOpacity={1} fill="url(#purchaseGrad)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Revenue by Category */}
        <div className="rounded-2xl border border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-bold text-slate-900 dark:text-white font-display">
                Revenue & Margin by FMCG Category
              </h3>
              <p className="text-xs text-slate-500">Fast-turning kirana groceries breakdown</p>
            </div>
            <Link to="/analytics" className="text-xs text-emerald-600 dark:text-emerald-400 font-semibold hover:underline">
              Full Analytics →
            </Link>
          </div>

          <div className="h-64 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={categoryData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.2)" />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip
                  formatter={(val) => [`₹${val.toLocaleString("en-IN")}`, "Revenue"]}
                  contentStyle={{ backgroundColor: "#0f172a", borderRadius: "12px", border: "none", color: "#fff", fontSize: "12px" }}
                />
                <Bar dataKey="revenue" fill="#10b981" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Warehouse Stock Value Split */}
        <div className="rounded-2xl border border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-bold text-slate-900 dark:text-white font-display">
                Stock Valuation Across Warehouses
              </h3>
              <p className="text-xs text-slate-500">Bhiwandi Central vs Kurla Depot vs Dadar Shop</p>
            </div>
            <Link to="/warehouse" className="text-xs text-emerald-600 dark:text-emerald-400 font-semibold hover:underline">
              View Map →
            </Link>
          </div>

          <div className="h-64 w-full flex items-center justify-center">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={warehouseDistribution}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={85}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {warehouseDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(val) => [`₹${val.toLocaleString("en-IN")}`, "Value"]}
                  contentStyle={{ backgroundColor: "#0f172a", borderRadius: "12px", border: "none", color: "#fff", fontSize: "12px" }}
                />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Fast vs Slow Moving Leaderboard */}
        <div className="rounded-2xl border border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-bold text-slate-900 dark:text-white font-display">
                Fast vs Slow Moving Velocity
              </h3>
              <p className="text-xs text-slate-500">Stock turnover rankings</p>
            </div>
            <span className="text-[11px] font-mono text-slate-400">Inventory Index</span>
          </div>

          <div className="space-y-3 text-xs">
            <div className="space-y-1">
              <div className="flex justify-between font-semibold">
                <span className="text-emerald-600 dark:text-emerald-400">🔥 Daawat Basmati Rice (5kg)</span>
                <span>4.8x Turnover/mo</span>
              </div>
              <div className="w-full bg-slate-100 dark:bg-slate-800 h-2 rounded-full overflow-hidden">
                <div className="bg-emerald-500 h-full rounded-full w-[94%]" />
              </div>
            </div>

            <div className="space-y-1">
              <div className="flex justify-between font-semibold">
                <span className="text-emerald-600 dark:text-emerald-400">🔥 Fortune Refined Sunflower Oil</span>
                <span>4.2x Turnover/mo</span>
              </div>
              <div className="w-full bg-slate-100 dark:bg-slate-800 h-2 rounded-full overflow-hidden">
                <div className="bg-emerald-500 h-full rounded-full w-[85%]" />
              </div>
            </div>

            <div className="space-y-1">
              <div className="flex justify-between font-semibold">
                <span className="text-emerald-600 dark:text-emerald-400">🔥 Tata Salt (1kg)</span>
                <span>3.9x Turnover/mo</span>
              </div>
              <div className="w-full bg-slate-100 dark:bg-slate-800 h-2 rounded-full overflow-hidden">
                <div className="bg-emerald-500 h-full rounded-full w-[78%]" />
              </div>
            </div>

            <div className="space-y-1 pt-1">
              <div className="flex justify-between font-semibold">
                <span className="text-rose-500">❄️ Parle-G Gold Biscuits (Slow)</span>
                <span>0.3x Turnover/mo</span>
              </div>
              <div className="w-full bg-slate-100 dark:bg-slate-800 h-2 rounded-full overflow-hidden">
                <div className="bg-rose-500 h-full rounded-full w-[14%]" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
